import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { Gift, Download, Mail, Check, Ban, BadgeCheck } from 'lucide-react';
import { db, sendGraceHavenEmail } from '@/lib/firebase';
import { useBusiness } from '@/lib/business';
import { formatPula } from '@/lib/money';
import { downloadGiftCard, giftCardBase64, giftCardFilename } from '@/lib/giftCardPdf';
import type { GiftCard, GiftCardStatus } from '@/lib/types';

const STATUS: Record<GiftCardStatus, string> = {
  requested: 'bg-espresso/10 text-espresso',
  active: 'bg-gold/15 text-gold-deep',
  redeemed: 'bg-emerald-100 text-emerald-700',
  void: 'bg-blush-soft text-blush-deep line-through',
};
const genCode = () => 'GH-GIFT-' + Math.random().toString(16).slice(2, 8).toUpperCase();

export function GiftCards() {
  const biz = useBusiness();
  const [cards, setCards] = useState<GiftCard[]>([]);
  const [filter, setFilter] = useState<'all' | GiftCardStatus>('all');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [issuing, setIssuing] = useState<GiftCard | null>(null);
  const [payMethod, setPayMethod] = useState('Cash');
  const [payRef, setPayRef] = useState('');
  const [payAmount, setPayAmount] = useState(0);
  const [payDate, setPayDate] = useState('');
  const [payNote, setPayNote] = useState('');

  useEffect(() => {
    const q = query(collection(db, 'giftCards'), orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => setCards(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<GiftCard, 'id'>) }))),
      () => setCards([]),
    );
  }, []);

  const shown = useMemo(() => (filter === 'all' ? cards : cards.filter((c) => c.status === filter)), [cards, filter]);
  function flash(m: string) { setToast(m); setTimeout(() => setToast(''), 3500); }

  function openIssue(c: GiftCard) {
    setIssuing(c);
    setPayMethod('Cash');
    setPayRef('');
    setPayAmount(c.amount);
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayNote('');
  }
  async function confirmIssue() {
    if (!issuing) return;
    setBusy(true);
    try {
      const data: Record<string, unknown> = {
        code: issuing.code || genCode(), status: 'active', balance: issuing.amount,
        paymentMethod: payMethod, amountPaid: Number(payAmount) || issuing.amount,
        paidAt: payDate || new Date().toISOString(),
        issuedAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
      };
      if (payRef.trim()) data.paymentRef = payRef.trim();
      if (payNote.trim()) data.paymentNote = payNote.trim();
      await updateDoc(doc(db, 'giftCards', issuing.id), data);
      setIssuing(null);
      flash('Gift card issued and payment recorded.');
    } catch (e) { flash(e instanceof Error ? e.message : 'Failed.'); } finally { setBusy(false); }
  }

  async function setStatus(c: GiftCard, status: GiftCardStatus) {
    try {
      await updateDoc(doc(db, 'giftCards', c.id), { status, updatedAt: new Date().toISOString() });
      flash(`Marked ${status}.`);
    } catch (e) { flash(e instanceof Error ? e.message : 'Failed.'); }
  }

  async function emailVoucher(c: GiftCard) {
    if (!c.recipientEmail) return flash('No recipient email on this card.');
    if (!c.code) return flash('Issue the card first to generate a code.');
    setBusy(true);
    try {
      const base64 = giftCardBase64(c, biz);
      const body =
        `Dear ${c.recipientName.split(' ')[0] || 'there'},\n\n` +
        `${c.fromName ? `${c.fromName} has` : 'You have'} been given a ${formatPula(c.amount)} gift card for ${biz.name}!\n\n` +
        `Your voucher is attached. Present it (code ${c.code}) when you book.\n\nRelax. Refresh. Rejuvenate.`;
      await sendGraceHavenEmail({
        to: c.recipientEmail,
        subject: `A gift for you — ${biz.name}`,
        body,
        attachments: [{ filename: giftCardFilename(c), contentBase64: base64, contentType: 'application/pdf' }],
        relatedEntityType: 'giftCard', relatedEntityId: c.id,
      });
      await updateDoc(doc(db, 'giftCards', c.id), { emailSentAt: new Date().toISOString(), updatedAt: new Date().toISOString() });
      flash(`Voucher emailed to ${c.recipientEmail}.`);
    } catch (e) { flash(e instanceof Error ? e.message : 'Email failed (check email config).'); } finally { setBusy(false); }
  }

  return (
    <div>
      <h1 className="font-display text-3xl text-espresso">Gift Cards</h1>
      <p className="mt-1 font-body text-sm text-muted">Issue vouchers, email them, and track redemption.</p>

      <div className="mt-6 flex flex-wrap gap-2">
        {(['all', 'requested', 'active', 'redeemed', 'void'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={`chip ${filter === f ? 'border-gold bg-gold/15 text-espresso' : 'border-line bg-white text-muted hover:border-gold'}`}>{f}</button>
        ))}
      </div>

      <div className="mt-5 overflow-x-auto border border-line bg-paper">
        <table className="w-full min-w-[680px] text-left">
          <thead>
            <tr className="border-b border-line font-body text-[11px] uppercase tracking-wide text-muted">
              <th className="px-5 py-3">Card</th><th className="px-5 py-3">Recipient</th><th className="px-5 py-3">Amount</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((c) => (
              <tr key={c.id} className="border-b border-line last:border-0 hover:bg-cream/40">
                <td className="px-5 py-3.5">
                  <span className="flex items-center gap-2 font-body text-sm font-semibold text-espresso"><Gift size={15} className="text-gold-deep" /> {c.code || '—'}</span>
                  {c.fromName && <span className="font-body text-xs text-muted">from {c.fromName}</span>}
                </td>
                <td className="px-5 py-3.5">
                  <p className="font-body text-sm text-espresso">{c.recipientName}</p>
                  <p className="font-body text-xs text-muted">{c.recipientEmail || '—'}</p>
                </td>
                <td className="px-5 py-3.5 font-display text-base text-espresso">{formatPula(c.amount)}</td>
                <td className="px-5 py-3.5"><span className={`inline-block rounded-none px-2.5 py-1 font-body text-[10px] font-bold uppercase tracking-wide ${STATUS[c.status]}`}>{c.status}</span></td>
                <td className="px-5 py-3.5">
                  <div className="flex items-center justify-end gap-3 text-muted">
                    {c.status === 'requested' && (
                      <button onClick={() => openIssue(c)} disabled={busy} title="Issue card" className="hover:text-gold-deep"><BadgeCheck size={16} /></button>
                    )}
                    {c.code && <button onClick={() => downloadGiftCard(c, biz)} title="Download voucher" className="hover:text-espresso"><Download size={16} /></button>}
                    {c.code && c.status !== 'void' && <button onClick={() => emailVoucher(c)} disabled={busy} title="Email voucher" className="hover:text-gold-deep"><Mail size={16} /></button>}
                    {c.status === 'active' && <button onClick={() => setStatus(c, 'redeemed')} title="Mark redeemed" className="hover:text-emerald-600"><Check size={16} /></button>}
                    {c.status !== 'void' && <button onClick={() => setStatus(c, 'void')} title="Void" className="hover:text-blush-deep"><Ban size={16} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {shown.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center font-body text-sm text-muted">No gift cards yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {issuing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-espresso-deep/60 p-4" onClick={() => setIssuing(null)}>
          <div className="w-full max-w-md border border-line bg-cream p-7 shadow-soft" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-display text-2xl text-espresso">Record payment & issue</h2>
            <p className="mt-1 font-body text-sm text-muted">For {issuing.recipientName} · {formatPula(issuing.amount)}. These details are kept for audit.</p>
            <div className="rule-gold mt-3" />
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="label">Payment method</label>
                <select className="field" value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
                  {['Cash', 'Card', 'Bank transfer', 'EFT', 'Mobile money', 'Other'].map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Amount paid</label>
                <input className="field" type="number" value={payAmount || ''} onChange={(e) => setPayAmount(e.target.value === '' ? 0 : Number(e.target.value))} />
              </div>
              <div>
                <label className="label">Reference (optional)</label>
                <input className="field" value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="Receipt / txn no." />
              </div>
              <div>
                <label className="label">Date paid</label>
                <input className="field" type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} />
              </div>
              <div className="sm:col-span-2">
                <label className="label">Note (optional)</label>
                <input className="field" value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Anything to record" />
              </div>
            </div>
            <div className="mt-6 flex gap-2.5">
              <button onClick={() => setIssuing(null)} className="btn-outline flex-1 justify-center">Cancel</button>
              <button onClick={confirmIssue} disabled={busy} className="btn-primary flex-1 justify-center">{busy ? 'Issuing…' : 'Issue & record'}</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>}
    </div>
  );
}
